import { useState } from 'react';

import { EMAIL_VERIFICATION } from '../../../../shared/featureFlags/flagNames';
import { BannerStyle } from '../../../../typings/enums';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useResendEmailVerification } from '../../../hooks/api/auth/email/useResendEmailVerification';
import Banner from '../Banner';
import Button from '../Button/Button';
import { BUTTON_TYPE } from '../Button/button-constants';

// Registration fires off a verification email automatically.
// Hide the banner briefly afterwards so a freshly-signed-up user doesn't hit the resend cooldown (60s on the backend) on their first click.
const POST_SIGNUP_SUPPRESSION_MS = 2 * 60 * 1000;

const isRecentSignup = (): boolean => {
  const joined = window.user?.joined;
  if (!joined) {
    return false;
  }
  const joinedAt = new Date(joined).getTime();
  if (Number.isNaN(joinedAt)) {
    return false;
  }
  return Date.now() - joinedAt < POST_SIGNUP_SUPPRESSION_MS;
};

/**
 * Shown to the currently logged-in user when their own row has not been
 * email-verified. Verification happens by clicking the link in the email, this banner just gets the email sent.
 */
const EmailVerificationBanner = () => {
  const [isDismissed, setDismissed] = useState(false);
  const isFlagEnabled = useIsFeatureEnabled(EMAIL_VERIFICATION);
  const { mutate: resend, isPending: isResending } = useResendEmailVerification({
    onSkipped: () => setDismissed(true),
  });

  const shouldShow = !!(
    !isDismissed
    && isFlagEnabled
    && window.user
    && window.user.email_verified !== true
    && !window.on_prem
    && !isRecentSignup()
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <Banner
      title="Verify your email address"
      body="We'll send a link to your email. Click it to verify your account and keep access to your organizations."
      bannerStyle={BannerStyle.Medium}
      actions={
        <Button
          type={BUTTON_TYPE.OUTLINE_LIGHT}
          onClick={() => resend()}
          isDisabled={isResending}
          >
          Send verification email
        </Button>
      }
    />
  );
};

export default EmailVerificationBanner;
